import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getMyAssignmentsUnviewedCount } from '../services/assignmentService';
import { createLogger } from '../utils/logger';

const logger = createLogger('AssignmentCountContext');
const POLL_INTERVAL_MS = 60_000;

interface AssignmentCountContextType {
    count: number;
    refetch: () => Promise<void>;
}

const AssignmentCountContext = createContext<AssignmentCountContextType | undefined>(undefined);

export const useAssignmentCount = (): AssignmentCountContextType => {
    const ctx = useContext(AssignmentCountContext);
    if (!ctx) throw new Error('useAssignmentCount must be used within AssignmentCountProvider');
    return ctx;
};

export const AssignmentCountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { token, isTrialUser } = useAuth();
    const [count, setCount] = useState(0);
    // A monotonically increasing id, rather than a boolean, so a fetch started for a
    // since-replaced token (e.g. after logout/login as a different user) can never
    // overwrite state once a newer fetch has been kicked off — resetting a shared
    // boolean on every effect run would let a late-resolving stale request win the race.
    const requestIdRef = useRef(0);

    const fetchCount = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        // Trial users have no assignments feature (hidden from the sidebar, blocked
        // server-side) — skip the poll entirely rather than hit a 403 every interval.
        if (!token || isTrialUser) { setCount(0); return; }
        try {
            const result = await getMyAssignmentsUnviewedCount(token);
            if (requestIdRef.current !== requestId) return;
            setCount(result);
        } catch (err) {
            if (requestIdRef.current !== requestId) return;
            logger.warn('Failed to fetch assignment count', err);
            setCount(0);
        }
    }, [token, isTrialUser]);

    useEffect(() => {
        if (!token || isTrialUser) {
            requestIdRef.current++;
            setCount(0);
            return;
        }

        fetchCount();
        const intervalId = setInterval(fetchCount, POLL_INTERVAL_MS);

        function handleVisibilityChange() {
            if (document.visibilityState === 'visible') {
                fetchCount();
            }
        }
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [token, isTrialUser, fetchCount]);

    return (
        <AssignmentCountContext.Provider value={{ count, refetch: fetchCount }}>
            {children}
        </AssignmentCountContext.Provider>
    );
};
